import React, {
  useState, useContext, useEffect, useCallback,
} from 'react';
import {
  Button,
  CardShowcase, Heading, IconFeedback, Textarea,
} from '@americanexpress/dls-react';
import { IconStar } from '@americanexpress/dls-icons';
import { withRouter } from '@americanexpress/one-app-router';
import PropTypes from 'prop-types';
import Wrapper from '../components/Wrapper';
import { PaymentContext } from '../utility/PaymentContext';
import { TokenContext } from '../utility/TokenContext';
import useApiWithSession from '../utility/useApiWithSession';

// Helper: Maximum feedback length
const MAX_FEEDBACK_LENGTH = 100;

const Feedback = ({ router }) => {
  // State and context
  const [selectedStars, setSelectedStars] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const starIds = ['star1', 'star2', 'star3', 'star4', 'star5'];
  const {
    sid1, market1, journey1, u1,
  } = useContext(PaymentContext);
  const { gateWayUrl } = useContext(TokenContext);

  // API hook
  const {
    isLoading, run, error, data,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: {
      eventName: 'EXPERIENCE_SUBMIT',
      eventData: {
        rating: String(selectedStars),
        feedback,
      },
    },
    customHeaders: {
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });

  // Redirect if sid1 is missing
  useEffect(() => {
    if (!sid1) {
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [router, sid1]);

  // Handle API response after submit
  useEffect(() => {
    if (!isLoading && submitted) {
      if (!error && data?.statusCode === 'success') {
        router.push({ pathname: '/message', query: { level: 1 } });
      } else if (error || data?.statusCode === 'failure') {
        router.push({ pathname: '/message', query: { level: 3 } });
      }
    }
  }, [isLoading, error, data, submitted, router]);

  // Handlers
  const handleStarClick = useCallback((index) => {
    setSelectedStars(index + 1);
  }, []);

  const handleFeedbackChange = useCallback((e) => {
    if (e.target.value.length <= MAX_FEEDBACK_LENGTH) {
      setFeedback(e.target.value);
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    await run();
    setSubmitted(true);
  }, [run]);

  return (
    <Wrapper>
      <CardShowcase className="anim-fade-in anim-delay-1 width-full pad-1 card-rounded margin-2-t">
        <div className="pad-1-lr pad-2-tb card-rounded flex flex-column" style={{ gap: '1rem' }}>
          <IconFeedback title="Example icon" titleId="example-icon-id" size="lg" />
          <Heading headingStyle="heading4" level={3}>We’d like your feedback</Heading>
          <p className="legal-1">Please rate your Paybill experience.</p>
          <div className="flex flex-justify-center flex-align-center" style={{ gap: '0.5rem' }}>
            {starIds.map((id, index) => (
              <IconStar
                key={`star-${id}`}
                title="Star icon"
                titleId={`star-icon-${id}`}
                isFilled={index < selectedStars}
                onClick={() => handleStarClick(index)}
                className="cursor-pointer"
                size="lg"
              />
            ))}
          </div>
          <p className="body-1">Do you have any other comments or feedback?</p>
          <form onSubmit={handleSubmit} className="width-full" style={{ position: 'relative' }}>
            <Textarea
              hint="Textarea hint"
              id="example-textarea-id"
              label="Textarea Label"
              status="default"
              statusMessage="Message."
              value={feedback}
              onChange={handleFeedbackChange}
              maxLength={MAX_FEEDBACK_LENGTH}
            />
            <span
              style={{
                position: 'absolute',
                right: '10px',
                fontSize: '0.9em',
                color: '#888',
                pointerEvents: 'none',
              }}
            >
              {`${feedback.length}/${MAX_FEEDBACK_LENGTH}`}
            </span>
            <Button
              type="submit"
              className="width-full margin-2-t"
              styleType="primary"
              style={{ maxWidth: '100%' }}
              disabled={selectedStars === 0 || isLoading || submitted}
            >
              <p className="body-3">Submit</p>
            </Button>
          </form>
        </div>
      </CardShowcase>
    </Wrapper>
  );
};

export default withRouter(Feedback);

Feedback.propTypes = {
  router: PropTypes.instanceOf(Object).isRequired,
};
